<template>
  <div class="writing-page">
    <TheNav />
    <TheActions />

    <div class="container">
      <TheBackLink />

      <div class="section-heading">
        <div class="heading-line"/>
        <h1 class="section-title">All Writing</h1>
      </div>
      <p class="section-subtitle">
        Published on Medium, X and LinkedIn. Every piece opens where it lives.
      </p>

      <WritingList
        v-if="posts.length || pending"
        :posts="posts"
        :pending="pending"
      />
      <p v-else class="writing-page__empty">Nothing published yet.</p>

      <!-- Under the list rather than above it. Somebody who has read down the
           page is the one worth asking; somebody who has not seen the writing
           has no reason to want more of it. -->
      <aside class="writing-page__signup">
        <h2 class="writing-page__signupTitle">Get the next one</h2>
        <NewsletterSignup source="writing-page" />
      </aside>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { personalInfo } from '~/data/content';

usePageScroll();

// Everything, in the order the endpoint returns it, which is newest first.
// The home panel sorts and slices for its top three; this is the full list.
//
// No compiled-in fallback any more. There used to be three placeholder
// articles here, and because live rows are appended after the fallback they
// sat permanently above the real writing on this page. Empty is the honest
// state when the database cannot be reached, and it is what dev shows, since
// the function this reads does not run under `nuxt dev`.
const posts = ref([]);
const { load, usePending } = useLiveContent();
const pending = usePending();

onMounted(async () => {
  posts.value = await load('posts');
});

useHead({
  title: `Writing - ${personalInfo.fullName}`,
  meta: [
    { name: 'description', content: `Articles and notes by ${personalInfo.fullName}` },
    { property: 'og:title', content: `Writing - ${personalInfo.fullName}` },
    { property: 'og:description', content: `Articles and notes by ${personalInfo.fullName}` },
    { name: 'twitter:title', content: `Writing - ${personalInfo.fullName}` },
    { name: 'twitter:description', content: `Articles and notes by ${personalInfo.fullName}` },
  ],
});
</script>

<style lang="scss" scoped>
// A normal vertical page: it sits outside the horizontal track, so it scrolls
// conventionally rather than being pinned.
.writing-page {
  min-height: 100vh;
  min-height: 100svh;
  padding: clamp(3rem, 8vh, 5rem) 0 clamp(3rem, 8vh, 5rem);
  background: transparent;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: clamp(0.5rem, 1.2vh, 0.75rem);

  .heading-line {
    // Hidden site-wide, as everywhere else. Kept so it can be brought back by
    // dropping this one line.
    display: none;
    width: 50px;
    height: 3px;
    flex-shrink: 0;
    background: linear-gradient(
      90deg,
      rgba(var(--accent-rgb), 0) 0%,
      var(--accent) 40%,
      var(--accent) 60%,
      rgba(var(--accent-rgb), 0) 100%
    );
    clip-path: polygon(0 50%, 14% 0, 86% 0, 100% 50%, 86% 100%, 14% 100%);
  }

  .section-title {
    font-size: clamp(1.5rem, 1.6vw + 1.6vh, 2.25rem);
    font-weight: 800;
    color: var(--text);
    margin: 0;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 4px;
    background: linear-gradient(135deg, var(--accent) 0%, rgba(var(--accent-rgb), 0.8) 50%, var(--text) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
  }
}

.section-subtitle {
  text-align: left;
  color: var(--muted);
  font-size: clamp(0.8rem, 0.5vw + 0.8vh, 1rem);
  max-width: 620px;
  margin: 0 0 clamp(1.5rem, 3vh, 2.5rem);
}

.writing-page__empty {
  color: var(--muted);
  font-size: 0.9rem;
}

.writing-page__signup {
  margin-top: clamp(2rem, 6vh, 3.5rem);
  padding-top: clamp(1.5rem, 4vh, 2.5rem);
  border-top: 1px solid rgba(var(--border-rgb), 0.3);
}

.writing-page__signupTitle {
  margin: 0 0 0.6rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
}

@media (max-width: 768px) {
  .section-heading {
    gap: 0.6rem;

    .section-title {
      font-size: clamp(0.95rem, 4.2vw, 1.25rem);
      letter-spacing: 2px;
    }
  }
}
</style>
